<template>
  <div id="results-container">
    <div class="input-options">
      <div id="postcode-constituency" class="option curved">
        <h3>Explore by constituency</h3>
        <div class="postcode-div">
          <input
            id="postcode"
            type="text"
            class="postcode"
            v-model="postcode"
            placeholder="Search by postcode"
          />
          <button type="submit" v-on:click="getConstituency()">
            <i class="fa fa-search"></i>
          </button>
        </div>
        <select
          v-model="constituency_selected"
          @change="constituencyChange"
          name="constituency"
          id="constituency"
        >
          <option disabled value="">Select a constituency</option>
          <option v-for="c in constituencies" :key="c.selection">
            {{ c.selection }}
          </option>
        </select>
      </div>
      <div
        class="results curved-bottom"
        v-bind:class="{ shown: results_chosen }"
      >
        <h3>Results</h3>
        <p
          v-bind:class="{ shown: constituency_chosen }"
          ref="constituencyText"
        ></p>
        <p v-bind:class="{ shown: region_chosen }" ref="regionText"></p>
      </div>
      <div class="method">
        <p>20,022 people were polled in December 2020 with results weighted to be nationally representative. 
          Constituency estimates are available for England, Scotland and Wales and were obtained using MRP.
        </p>
        <div class="btn-container">  
          <div class="results-btn">        
            <p>Full results are <a href="https://demos.co.uk/wp-content/uploads/2021/04/Full-constituency-results-download.xlsx" onclick="window.fathom.trackGoal('B7FJLUNJ', 0);">available here</a></p>
          </div>
            <div class="report-btn">
              <a href="https://demos.co.uk/wp-content/uploads/2021/04/Everyday-Places.pdf" exact class="report-router">
                <p class="explore-link s1 mr yellow-bg">
                  Read the report
                </p>
              </a>
            </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import json_regions from "../json/regions.json";
import json_constituencies from "../json/constituencies.json";

export default {
  name: "InputOptions",
  inject: ["sitearea"],
  methods: {
    getRegionText(region) {
      let blurb;
      switch (this.sitearea) {
        case "mosturgent":
          blurb =
            "Generally in <span class='result'>" +
            region.text +
            "</span> people think that the most important priority for improvement in their local area is <span class='result'>" +
            region.priority +
            "</span>" +
            "<br/><br/>" +
            "In general they are " +
            "<span class='result'>" +
            region.positivity +
            "</span> " +
            "than average about how their local area measures up against the things they think are important." + 
            "<br/><br/>To see results in another area, type in a different postcode or choose a different constituency.";
          break;
        case "covideffect":
          blurb =
            "In " +
            "<span class='result'>" +
            region.text +
            ", " +
            region.movers +
            "</span> " +
            "of people have either moved for reasons relating to the pandemic or plan to do so. A further " +
            "<span class='result'>" +
            region.nearlyMovers +
            "</span> " +
            "say they are thinking about moving for reasons related to the pandemic but have no solid plans. The most commonly cited reason for moving in " +
            "<span class='result'>" +
            region.text +
            "</span> is " +
            "<span class='result'>" +
            region.reason +
            "</span>.";
          break;
        case "satisfaction":
          blurb =
            "People in " +
            "<span class='result'>" +
            region.text +
            "</span> " +
            "were generally " +
            "<span class='result'>" +
            region.positivity +
            "</span> " +
            "than average about how their local area measured up against the things they thought were important. The average score in " +
            region.text +
            " was " +
            "<span class='result'>" +
            region.average +
            "</span> " +
            "compared to +26 for the country as a whole, where a score of -100 is very bad and +100 is very good. This difference is mainly because people in " +
            region.text +
            " were " +
            "<span class='result'>" +
            region.explanation +
            "</span> " +
            "than elsewhere in Britain.";
          break;
      }
      return blurb;
    },

    getConstituencyText(constituency) {
      let blurb;
      switch (this.sitearea) {
        case "mosturgent":
          blurb =
            "People in <span class='result'>" +
            constituency.text +
            "</span> constituency think that the most important priority for improvement in their local area is " +
            "<span class='result'>" +
            constituency.priority +
            "</span>" + 
            "<br/><br/>" +
            "In general they are " +
            "<span class='result'>" +
            constituency.attitude +
            "</span> " +
            "than average about how their local area measures up against the things they think are important.<br/><br/>"
            ;
          break;
        case "covideffect":
          blurb =
            "In " +
            "<span class='result'>" +
            constituency.text +
            " </span>" +
            "constituency, " +
            "<span class='result'>" +
            constituency.movers +
            "</span> " +
            "of people have either moved for reasons relating to the pandemic or plan to do so. A further " +
            "<span class='result'>" +
            constituency.nearlyMovers +
            "</span> " +
            "say they are thinking about moving for reasons related to the pandemic but have no solid plans.";
          break;
        case "satisfaction":
          blurb =
            "People in " +
            "<span class='result'>" +
            constituency.text +
            " </span> " +
            "constituency were " +
            "<span class='result'>" +
            constituency.attitude +
            "</span> " +
            "than average about how their local area measured up against the things they thought were important.";
          break;
      }
      return blurb;
    },
    regionChange(chosen) {
      let our_region = this.regions.find(r => {
        return r.selection === chosen;
      });
      this.$refs.regionText.innerHTML = this.getRegionText(our_region);
      this.results_chosen = true;
      this.region_chosen = true;
    },
    constituencyChange() {
      let our_constituency = this.constituencies.find(c => {
        return c.selection === this.constituency_selected;
      });
      this.$refs.constituencyText.innerHTML = this.getConstituencyText(
        our_constituency
      );
      this.results_chosen = true;
      this.constituency_chosen = true;
      this.regionChange(our_constituency.region);
    },
    getConstituency() {
      fetch("https://api.postcodes.io/postcodes/" + this.postcode)
        .then(response => {
          if (response.ok) {
            return response.json();
          }
        })
        .then(response => {
          let result = response.result.parliamentary_constituency;
          let region_from_constituency = response.result.region;
          this.regionChange(region_from_constituency);
          this.constituency_selected = result;
          this.constituencyChange();
        })
        .catch(err => {
          console.log(err);
        });
    }
  },
  data() {
    return {
      region_selected: "",
      constituency_selected: "",
      postcode: "",
      regions: json_regions,
      constituencies: json_constituencies,
      region_chosen: false,
      constituency_chosen: false,
      region_from_constituency: "",
      results_chosen: false,
      region_text: ""
    };
  }
};
</script>

<style scoped>

  h3 {
    text-transform: uppercase;
    padding-bottom: 1em;
    padding-top: 0.3em;
  }

  label {
    margin-left: 5px;
    margin-bottom: 3px;
  }

  .input-options {
    display: flex;
    flex-wrap: wrap;
  }

  .option {
    display: flex;
    flex-direction: row;
    background-color: #feebb3;
    padding: 1.5rem;
    min-width: 200px;
    border-radius: 15px;
    width: 100%;
    margin: 2rem 0rem;
  }

  .option.last {
    margin-right: 0;
  }

  .postcode {
    width: 20ch;
  }

  .postcode-div,
  #constituency {
    display: flex;
    margin-left: 50px;
    height: 36px;
    color: #1d3336;
  }

  select {
    max-width: 266px;
  }

  .results,
  .results p {
    display: none;
  }

  .shown,
  .results p.shown {
    display: block;
  }

  .curved {
    border-radius: 15px;
  }

  .curved-top {
    border-radius: 15px 15px 0 0;
  }

  .curved-bottom {
    border-radius: 0 0 15px 15px;
  }

  .results.shown {
    background-color: white;
    padding: 1em;
    width: 100%;
    /*max-width: 893px;*/
    margin-top: -15px;
  }

  input.postcode {
    border-radius: 4px 0 0 4px;
  }

  .results p {
    padding-bottom: 1em;
  }

  #results-container {
    padding-right: 2rem;
    margin-bottom: 1.5em;
  }

  .method{
    width: 450px;
  }

  .method > * {
    margin: 1.5rem 0rem;
  }

  .results-btn a {
    text-decoration: underline;
    color: #1d3336;

  }

  .method, .btn-container p {
    font-size: 11pt;
  }

  ::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: #1d3336;
    opacity: 1; /* Firefox */
  }

  :-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    color: #1d3336;
  }

  ::-ms-input-placeholder {
    /* Microsoft Edge */
    color: #1d3336;
  }

  @media screen and (max-width: 850px) {
    .option {
      flex-direction: column;
    }

    .postcode-div,
    #constituency {
      margin: 0 0 1em 0;
    }
  }

  input:focus, button:focus, select.focus {
    outline-color: #EE7155;
  }

  .method > a {
    text-decoration: none;
  }

  p {
    width: fit-content;
  }

  .explore-link {
    font-weight: bold;
    cursor: pointer;
    text-transform: uppercase;
    padding: 10px;
    margin: auto;
  }

  .btn-container {
    margin-top: 1rem;
  }

  .report-btn, .results-btn {
    color: #1d3336;  
    width: fit-content;
  }

  .report-btn {
    margin-top: 1rem;
  }

  .report-router {
    text-decoration: none;
  }

  .s1 {
    text-align: end;
  }

  .s1,
  .s2 {
    color: #1d3336;
  }

  .mr {
    margin-left: auto;
  }

  .yellow-bg {
    background-color: #feebb3;
    color: #1d3336;
  }

  @media screen and (min-width: 1229px){
    #constituency, #postcode{
      margin-left: 10rem;
    }
  }

</style>
